import { toFacetMap } from '@/helpers/search/facets'
import type { Locale } from 'lc-services/types'

export const useSearchNoResult = () => {
  const { $i18n } = useNuxtApp()
  const { locale, t } = useI18n()
  // Search input
  const {
    data,
    destinationId,
    isSearchingForUnknownDestination,
    setData,
    unknownDestination,
  } = useSearchInput()
  // Filters
  const { clearBudgetFilters, hasBudget, price } = useSearchBudgetFilters()
  const { budgetFormatted, hasRooms, roomsFormatted } = useSearchClientHeader()
  const { clearDateFilters, endDate, hasDates, startDate } =
    useSearchDateFilters()
  const { apiParamsDestinationIds } = useSearchMultipleLocations()
  const { facetFilters, setFacetFilter } = useSearchFacetFilters()
  const { bathrooms, bedrooms, capacity, clearNumericFilterByType } =
    useSearchNumericFilters()
  const { bounds, geographicAreaActive, resetBounds } = useSearchSplitMap()

  const allSelectedFilters = computed(() =>
    [
      {
        active: Boolean(destinationId.value),
        name: 'location',
        onRemove: () => {
          setData('location', {})
        },
        queryModel: { destinationIds: apiParamsDestinationIds.value },
        text: (data.value.location.name ?? '').split(',')[0],
      },
      {
        active:
          geographicAreaActive.value && !isSearchingForUnknownDestination.value,
        name: 'geocoding',
        onRemove: () => {
          setData('location', {})
          resetBounds()
        },
        queryModel: { insideBoundingBox: [bounds.value] as [number[]] },
        text: $i18n.t('search.geographicArea'),
      },
      {
        active: isSearchingForUnknownDestination.value,
        name: 'unknown_destination',
        onRemove: () => {
          setData('location', {})
          resetBounds()
        },
        queryModel: {
          aroundRadius: 2e5,
          aroundLatLng: data.value.location.coords,
        },
        text: unknownDestination.value?.queryName,
      },
      {
        active: hasDates.value,
        name: 'dates',
        onRemove: () => {
          if (data.value.startDate) setData('startDate', null)
          if (data.value.endDate) setData('endDate', null)
          clearDateFilters()
        },
        queryModel:
          startDate.value && endDate.value
            ? {
                period: { startDate: startDate.value, endDate: endDate.value },
              }
            : {},
        text: t(
          'filters.dates',
          formatSearchDates([startDate.value, endDate.value], locale),
        ),
      },
      {
        active: hasBudget.value,
        name: 'budget',
        onRemove: clearBudgetFilters,
        queryModel: { prices: price.value },
        text: budgetFormatted.value,
      },
      {
        active: capacity.value > 1,
        name: 'guests',
        onRemove: () => {
          if (data.value.guest) setData('guest', 0)
          clearNumericFilterByType('capacity')
        },
        queryModel: { capacity: capacity.value?.toString() },
        text: t('filters.guests', capacity.value),
      },
      {
        active: hasRooms.value,
        name: 'rooms',
        queryModel: {
          bathrooms: bathrooms.value?.toString(),
          bedrooms: bedrooms.value?.toString(),
        },
        onRemove: () => {
          clearNumericFilterByType('bathrooms')
          clearNumericFilterByType('bedrooms')
        },
        text: roomsFormatted.value,
      },
      ...Object.entries(facetFilters.value)
        .flatMap(([key, filters]) =>
          filters.map((facet) => {
            const [tag, value] = facet.split(': ') as [
              `searchable_tags.${number}.${Locale}`,
              string,
            ]
            return {
              active: Boolean(tag && value),
              name: facet,
              onRemove: () =>
                setFacetFilter(
                  { key: Number(key), tag, values: [] },
                  { checked: false, value },
                ),
              queryModel: {
                facetFilters: toFacetMap([facet]),
              },
              text: (value ?? '').trim(),
            }
          }),
        )
        .toSorted((a, b) => (a.text < b.text ? -1 : 1)),
    ].filter(({ active }) => active),
  )

  const clearAllFilters = async () => {
    // use a microtask to remove all filters in the same tick
    await new Promise<void>((resolve) =>
      queueMicrotask(() => {
        for (const { onRemove } of allSelectedFilters.value) {
          onRemove()
        }
        resolve()
      }),
    )
    // wait for vue to update filter states
    await nextTick()
  }

  return { allSelectedFilters, clearAllFilters }
}
